import { Suspense, lazy } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter, Navigate, Route, Routes, useMatch } from 'react-router-dom';
import { AuthProvider, landingFor, useAuth } from './features/auth/AuthContext.tsx';
import { can, type Permission } from '@aq/shared';
import { LoginPage } from './features/auth/LoginPage.tsx';
import { AppShell } from './app/AppShell.tsx';
import { NeedsLargerScreen } from './components/NeedsLargerScreen.tsx';
import { useIsSmallScreen } from './lib/useMediaQuery.ts';
import { DrivePage } from './features/drive/DrivePage.tsx';
import { SharedPage } from './features/drive/SharedPage.tsx';
import { TrashPage } from './features/drive/TrashPage.tsx';
/**
 * The two editors are loaded on demand.
 *
 * Between them they are most of the application's code — a spreadsheet engine
 * and grid, and a rich-text editor — and neither is needed to sign in, look
 * at the Drive or read the dashboard. Splitting them keeps the first load
 * small on the connections this will actually run over.
 */
const SheetPage = lazy(() =>
  import('./features/sheets/SheetPage.tsx').then((module) => ({ default: module.SheetPage })),
);
const DocumentPage = lazy(() =>
  import('./features/documents/DocumentPage.tsx').then((module) => ({
    default: module.DocumentPage,
  })),
);
import { DashboardPage } from './features/dashboard/DashboardPage.tsx';
import { StoragePage } from './features/storage/StoragePage.tsx';
import { StarredPage } from './features/drive/StarredPage.tsx';
import { UsersPage } from './features/users/UsersPage.tsx';
import { ProfilePage } from './features/profile/ProfilePage.tsx';
import { SecurityPage } from './features/security/SecurityPage.tsx';
import { SessionsPage } from './features/security/SessionsPage.tsx';
import { AuditPage } from './features/audit/AuditPage.tsx';
import { Spinner } from './components/ui.tsx';
import { DialogProvider } from './components/Dialog.tsx';
import { ToastProvider } from './components/Toast.tsx';
import { UploadProvider } from './features/drive/UploadQueue.tsx';
import { DriveClipboardProvider } from './features/drive/DriveClipboard.tsx';
import { ApiError } from './lib/api.ts';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      // Retrying a 4xx just repeats a rejected request. Only transient
      // failures are worth a second attempt — which includes status 0, the
      // client's marker for "never reached the server", since a dev server
      // restarting mid-request is exactly the case a retry recovers from.
      retry: (failureCount, error) => {
        if (error instanceof ApiError && error.status !== 0 && error.status < 500) return false;
        return failureCount < 2;
      },
      refetchOnWindowFocus: false,
    },
  },
});

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        {/* Overlays sit above routing so any screen can raise a dialog or a
            toast without threading callbacks down through the tree. */}
        <ToastProvider>
          <DialogProvider>
            <AuthProvider>
              {/* Above the router on purpose: an upload has to outlive the
                  screen that started it, or navigating away cancels it. */}
              {/* Both live above the router: an upload has to outlive the
                  screen that started it, and a clipboard filled in one folder
                  is pasted in another. */}
              <UploadProvider>
                <DriveClipboardProvider>
                  <Router />
                </DriveClipboardProvider>
              </UploadProvider>
            </AuthProvider>
          </DialogProvider>
        </ToastProvider>
      </BrowserRouter>
    </QueryClientProvider>
  );
}

/** What an editor shows while its own code is still arriving. */
function Editor({
  what,
  children,
}: {
  what: 'spreadsheet' | 'document';
  children: React.ReactNode;
}) {
  /**
   * Guarded here rather than inside the page so the editor never mounts on a
   * phone at all. Rendering it and covering it would still fetch the workbook,
   * build the grid and measure it — the client's mobile data spent on a screen
   * they were never going to see.
   */
  const small = useIsSmallScreen();
  if (small) return <NeedsLargerScreen what={what} />;

  return (
    <Suspense
      fallback={
        <div className="flex items-center gap-2 py-12 text-body-sm text-muted">
          <Spinner /> Opening…
        </div>
      }
    >
      {children}
    </Suspense>
  );
}

/**
 * A screen the account may not have, answered without rendering it.
 *
 * Rendering and then redirecting would fire the page's queries on the way
 * past, which for these two means asking the server for totals it is about to
 * refuse — a 403 in the console and a flash of a page that was never theirs.
 */
function Only({ permission, element }: { permission: Permission; element: React.ReactNode }) {
  const { user } = useAuth();
  if (user && can(user.role, permission)) return <>{element}</>;
  return <Navigate to={landingFor(user?.role)} replace />;
}

function Router() {
  const { user, ready } = useAuth();

  /**
   * A shared link belongs to nobody, and is answered before anything else.
   *
   * Everything below this line assumes a session: the loading spinner while
   * one is checked, the login form when there is none. A customer opening a
   * link to an invoice has no session and never will, and showing them a
   * password box would be the end of the thing the link was for. So this route
   * is matched first, and the session is never consulted.
   */
  const shared = useMatch('/s/:token');
  if (shared) {
    return (
      <Routes>
        <Route path="/s/:token" element={<SharedPage />} />
      </Routes>
    );
  }

  // Hold the first paint until the session check resolves, so a signed-in
  // user reloading the page never sees the login form flash past.
  if (!ready) {
    return (
      <div className="flex h-screen items-center justify-center bg-canvas">
        <Spinner label="Loading" />
      </div>
    );
  }

  if (!user) return <LoginPage />;

  return (
    <Routes>
      <Route element={<AppShell />}>
        <Route path="/drive" element={<DrivePage />} />
        <Route path="/starred" element={<StarredPage />} />
        <Route path="/trash" element={<TrashPage />} />
        <Route
          path="/sheets/:id"
          element={
            <Editor what="spreadsheet">
              <SheetPage />
            </Editor>
          }
        />
        <Route
          path="/documents/:id"
          element={
            <Editor what="document">
              <DocumentPage />
            </Editor>
          }
        />
        {/*
          Two screens that are facts about the system rather than about the
          paperwork, and a scoped account is told neither. The sidebar already
          leaves them out; this is for the address bar, which the sidebar
          cannot speak for. Sent to wherever that account does start rather
          than shown a refusal — there is nothing for them to do about it.
        */}
        <Route
          path="/dashboard"
          element={<Only permission="dashboard:read" element={<DashboardPage />} />}
        />
        <Route
          path="/storage"
          element={<Only permission="dashboard:read" element={<StoragePage />} />}
        />
        <Route path="/users" element={<UsersPage />} />
        <Route path="/profile" element={<ProfilePage />} />
        <Route path="/security" element={<SecurityPage />} />
        <Route path="/sessions" element={<SessionsPage />} />
        <Route path="/audit" element={<AuditPage />} />
      </Route>
      <Route path="*" element={<Navigate to={landingFor(user.role)} replace />} />
    </Routes>
  );
}
